import React from 'react';

import {
  type CollectionOverviewAnalyticsContextValue,
  trackCollectionOverviewInteraction,
} from '../ArtifactOverview/collectionOverviewAnalytics';
import {type ArtifactVersionMetricPanelEditorSession} from './ArtifactVersionMetricsContext';
import {
  type ArtifactVersionMetricGridPanel,
  type ArtifactVersionMetricGridPanelConfig,
  type ArtifactVersionsGridMetadata,
  createArtifactVersionMetricsPanel,
} from './artifactVersionsGridTypes';
import {
  getArtifactVersionMetricPanelsWithConfig,
  getConfiguredArtifactVersionMetricNames,
  getConfiguredArtifactVersionMetricPanels,
  getConfiguredArtifactVersionMetricPanelsForMembershipIds,
  hasConfiguredArtifactVersionMetricPanel,
  isConfiguredArtifactVersionMetricPanel,
} from './artifactVersionsGridUtils';
import {type ArtifactVersionMetricChartType} from './types';

type UpdateArtifactVersionsGridMetadata = (
  getNextMetadata: (
    metadata: ArtifactVersionsGridMetadata
  ) => ArtifactVersionsGridMetadata
) => void;

type UseArtifactVersionsGridPanelControllerArgs = {
  closePanelEditor: () => void;
  analyticsContext?: CollectionOverviewAnalyticsContextValue;
  displayedMembershipIds: string[];
  gridId: string;
  metadata: ArtifactVersionsGridMetadata;
  panelEditor: ArtifactVersionMetricPanelEditorSession | null;
  readOnly: boolean;
  syncPanelEditor: (session: ArtifactVersionMetricPanelEditorSession) => void;
  togglePanelEditor: (session: ArtifactVersionMetricPanelEditorSession) => void;
  updateMetadata: UpdateArtifactVersionsGridMetadata;
  updatePanelConfig: (
    panelId: string,
    config: Partial<ArtifactVersionMetricGridPanelConfig>
  ) => void;
};

export function useArtifactVersionsGridPanelController({
  analyticsContext,
  closePanelEditor,
  displayedMembershipIds,
  gridId,
  metadata,
  panelEditor,
  readOnly,
  syncPanelEditor,
  togglePanelEditor,
  updateMetadata,
  updatePanelConfig,
}: UseArtifactVersionsGridPanelControllerArgs) {
  const [fullscreenPanelId, setFullscreenPanelId] = React.useState<
    string | null
  >(null);
  const [pendingPanelEditorId, setPendingPanelEditorId] = React.useState<
    string | null
  >(null);
  const panelsToRender = readOnly
    ? getArtifactVersionMetricPanelsWithConfig(metadata.panels)
    : metadata.panels;
  const fullscreenPanels =
    getConfiguredArtifactVersionMetricPanelsForMembershipIds(
      panelsToRender,
      displayedMembershipIds
    );
  const fullscreenPanelIndex = fullscreenPanels.findIndex(
    panel => panel.__id__ === fullscreenPanelId
  );
  const fullscreenPanel =
    fullscreenPanelIndex >= 0 ? fullscreenPanels[fullscreenPanelIndex] : null;
  const hasConfiguredPanels = hasConfiguredArtifactVersionMetricPanel(metadata);
  const configuredMetricNames =
    getConfiguredArtifactVersionMetricNames(panelsToRender);
  const activePanelId = panelEditor?.panel.__id__;
  const ownsPanelEditorSession = panelEditor?.gridId === gridId;

  const syncEditorSession = React.useCallback(
    (
      panel: ArtifactVersionMetricGridPanel,
      initialChangedConfigKeys?: string[]
    ) => {
      syncPanelEditor({
        gridId,
        initialChangedConfigKeys,
        panel,
        membershipIds: metadata.membershipIds,
        updateConfig: (config: Partial<ArtifactVersionMetricGridPanelConfig>) =>
          updatePanelConfig(panel.__id__, config),
      });
    },
    [gridId, metadata.membershipIds, syncPanelEditor, updatePanelConfig]
  );

  const openPanelEditor = (panel: ArtifactVersionMetricGridPanel) => {
    trackCollectionOverviewInteraction(analyticsContext, 'edit_panel', {
      chartType: panel.config.chartType,
      gridId,
      panelId: panel.__id__,
      panelCount: metadata.panels.length,
      selectedVersionCount: metadata.membershipIds.length,
    });
    togglePanelEditor({
      gridId,
      panel,
      membershipIds: metadata.membershipIds,
      updateConfig: (config: Partial<ArtifactVersionMetricGridPanelConfig>) =>
        updatePanelConfig(panel.__id__, config),
    });
  };

  React.useEffect(() => {
    if (activePanelId == null || !ownsPanelEditorSession) {
      return;
    }

    const openPanel = metadata.panels.find(
      panel => panel.__id__ === activePanelId
    );
    if (openPanel == null) {
      closePanelEditor();
      return;
    }

    syncEditorSession(openPanel);
  }, [
    activePanelId,
    closePanelEditor,
    metadata.panels,
    ownsPanelEditorSession,
    syncEditorSession,
  ]);

  // A newly-added panel is committed to the Slate document asynchronously, so
  // wait for it to appear in metadata.panels and then open its editor. We never
  // cancel a pending open on unrelated panels changes (the previous logic did,
  // which could strand a just-added panel's editor); a superseding addPanel
  // simply overwrites the pending id.
  React.useEffect(() => {
    if (pendingPanelEditorId == null) {
      return;
    }

    const pendingPanel = metadata.panels.find(
      panel => panel.__id__ === pendingPanelEditorId
    );
    if (pendingPanel != null) {
      syncEditorSession(pendingPanel);
      setPendingPanelEditorId(null);
    }
  }, [metadata.panels, pendingPanelEditorId, syncEditorSession]);

  React.useEffect(() => {
    if (fullscreenPanelId != null && fullscreenPanel == null) {
      setFullscreenPanelId(null);
    }
  }, [fullscreenPanel, fullscreenPanelId]);

  const deletePanel = (panelId: string) => {
    const deletedPanel = metadata.panels.find(
      panel => panel.__id__ === panelId
    );
    if (
      panelEditor?.gridId === gridId &&
      panelEditor?.panel.__id__ === panelId
    ) {
      closePanelEditor();
    }
    updateMetadata(currentMetadata => ({
      ...currentMetadata,
      panels:
        currentMetadata.panels.length <= 1
          ? currentMetadata.panels
          : currentMetadata.panels.filter(panel => panel.__id__ !== panelId),
    }));
    trackCollectionOverviewInteraction(analyticsContext, 'delete_panel', {
      chartType: deletedPanel?.config.chartType,
      gridId,
      panelId,
      panelCount: metadata.panels.length,
      selectedVersionCount: metadata.membershipIds.length,
    });
  };

  const addPanel = (chartType: ArtifactVersionMetricChartType) => {
    let newPanel: ArtifactVersionMetricGridPanel | undefined;
    updateMetadata(currentMetadata => {
      newPanel = createArtifactVersionMetricsPanel({chartType});
      return {
        ...currentMetadata,
        panels: [...currentMetadata.panels, newPanel],
      };
    });
    if (newPanel != null) {
      setPendingPanelEditorId(newPanel.__id__);
      trackCollectionOverviewInteraction(analyticsContext, 'add_panel', {
        chartType,
        gridId,
        panelId: newPanel.__id__,
        panelCount: metadata.panels.length + 1,
        selectedVersionCount: metadata.membershipIds.length,
      });
    }
  };

  const selectPanelChartType = (
    panel: ArtifactVersionMetricGridPanel,
    chartType: ArtifactVersionMetricChartType
  ) => {
    const configuredPanel = {
      ...panel,
      config: {...panel.config, chartType},
    };
    updateMetadata(currentMetadata => ({
      ...currentMetadata,
      panels: currentMetadata.panels.map(existingPanel =>
        existingPanel.__id__ === panel.__id__ ? configuredPanel : existingPanel
      ),
    }));
    syncEditorSession(configuredPanel, ['chartType']);
  };

  const openFullscreenPanel = (panel: ArtifactVersionMetricGridPanel) => {
    if (
      !isConfiguredArtifactVersionMetricPanel(panel, displayedMembershipIds)
    ) {
      return;
    }

    setFullscreenPanelId(panel.__id__);
  };
  const closeFullscreenPanel = () => {
    setFullscreenPanelId(null);
  };
  const showPreviousFullscreenPanel = () => {
    if (fullscreenPanelIndex > 0) {
      setFullscreenPanelId(fullscreenPanels[fullscreenPanelIndex - 1].__id__);
    }
  };
  const showNextFullscreenPanel = () => {
    if (fullscreenPanelIndex < fullscreenPanels.length - 1) {
      setFullscreenPanelId(fullscreenPanels[fullscreenPanelIndex + 1].__id__);
    }
  };
  const canOpenFullscreenPanel = (panel: ArtifactVersionMetricGridPanel) =>
    isConfiguredArtifactVersionMetricPanel(panel, displayedMembershipIds);
  const canDeletePanel = !readOnly && metadata.panels.length > 1;

  return {
    addPanel,
    canDeletePanel,
    canOpenFullscreenPanel,
    configuredMetricNames,
    deletePanel,
    fullscreenPanel,
    fullscreenPanelIndex,
    fullscreenPanels,
    hasConfiguredPanels,
    openFullscreenPanel,
    openPanelEditor,
    ownsPanelEditorSession,
    selectPanelChartType,
    showNextFullscreenPanel,
    showPreviousFullscreenPanel,
    closeFullscreenPanel,
    panelsToRender,
  };
}
